import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, Grid, Header, Segment, Image } from 'semantic-ui-react';
import Typewriter from 'typewriter-effect';
import '../../../App.css';
import { fetchProfessorCardData } from '../../../util/FetchProfessorData';
import LoaderAnimation from '../../common/LoaderAnimation';
import SyllabusUploadModal from '../../common/modal/SyllabusUploadModal';
import ProfessorTitle from '../../common/titles/ProfessorTitle';
import RatingDistribution from '../../RatingDistribution';
import ProfessorSyllabi from './ProfessorSyllabi';
import CoursesList from './CoursesList';
import {
    ProfessorCardData,
    Quote,
    ProfessorCourse,
} from '../../interfaces/interfaces';
// @ts-ignore
import AIIcon from '../../../icons/ai-icon.svg';

interface ProfessorCardProps {
    professor_id: number;
    courses: ProfessorCourse[];
    setCourses: (courses: ProfessorCourse[]) => void;
    setQuotes: (quotes: Quote[]) => void;
}

const ProfessorCard: React.FC<ProfessorCardProps> = ({
    professor_id,
    courses,
    setCourses,
    setQuotes,
}) => {
    const [syllabusModalOpen, setSyllabusModalOpen] = useState(false);
    const [cardData, setCardData] = useState<ProfessorCardData | null>(null);
    const [cardDataLoading, setCardDataLoading] = useState(true);
    const [showOverview, setShowOverview] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        const getProfessorCardData = async () => {
            setCardDataLoading(true);
            try {
                const response = await fetchProfessorCardData(professor_id);
                const result: ProfessorCardData = await response.json();
                console.log(result);
                setCardData(result);
                setCourses(result.courses_taught);
                setQuotes(result.quotes);
            } catch (error) {
                console.error('Failed to fetch professor data:', error);
            } finally {
                setCardDataLoading(false);
            }
        };

        getProfessorCardData();
    }, [professor_id, setCourses, setQuotes]);

    const handleReviewClick = () => {
        if (!cardData) return;

        const { professor_id, first_name, last_name } =
            cardData.professor_summary.professor_header;

        navigate('/review/new', {
            state: {
                professorData: {
                    professor_id,
                    first_name,
                    last_name,
                    search_result_type: 'Professor',
                },
            },
        });
    };

    if (cardDataLoading || !cardData) {
        return (
            <Card fluid>
                <CardContent>
                    <LoaderAnimation />
                </CardContent>
            </Card>
        );
    }

    const { professor_header } = cardData.professor_summary;

    return (
        <>
            <Card fluid>
                <CardContent>
                    <Header as="h1">
                        <ProfessorTitle
                            professor={professor_header}
                            includeBadge
                        />
                    </Header>
                </CardContent>

                <CardContent>
                    <Grid columns={2} stackable>
                        <Grid.Row>
                            <Grid.Column mobile={16} tablet={8} computer={8}>
                                <CoursesList
                                    courses={cardData.courses_taught}
                                    onReviewClick={handleReviewClick}
                                    professorName={`${professor_header.first_name} ${professor_header.last_name}`}
                                />
                                <Header as="h2" content="Syllabi" />
                                <ProfessorSyllabi syllabi={cardData.syllabi} />
                                <br />
                                <Button
                                    className="blue-button"
                                    onClick={() => setSyllabusModalOpen(true)}
                                >
                                    Submit a Syllabus
                                </Button>
                            </Grid.Column>

                            <Grid.Column mobile={16} tablet={8} computer={8}>
                                <RatingDistribution
                                    mratings={cardData.ratings_histogram}
                                />
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </CardContent>
            </Card>
            <br/>
            <br/>

            {cardData.ai_overview && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '1.5em',
                        }}
                    >
                        <Header as="h2" style={{ margin: 0, display: 'flex', alignItems: 'center', paddingLeft: '0.1em' }}>
                            AI-Generated Summary
                            <Image
                                src={AIIcon}
                                alt="AI Icon"
                                style={{ marginLeft: '0.5em', width: '24px', height: '24px' }}
                            />
                        </Header>
                        <Button
                            style={{
                                backgroundColor: '#D6E9FA',
                                color: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5em',
                                marginLeft: '1em',
                            }}
                            icon={showOverview ? "eye slash" : "eye"}
                            content={showOverview ? "Hide Summary" : "Show Summary"}
                            onClick={() => setShowOverview(prev => !prev)}
                        />
                    </div>

                    <div
                        className={`overview-container ${showOverview ? 'show' : 'hide'}`}
                    >
                        <Segment className="gray-background">
                            {/* Blockquote disclaimer */}
                            <blockquote>
                                This summary was created with the assistance of AI and may contain inaccuracies or omissions.
                                Please refer to individual reviews for full context.
                            </blockquote>

                            {/* AI-Generated Text */}
                            <Typewriter
                                options={{
                                    strings: cardData.ai_overview,
                                    autoStart: true,
                                    delay: 1, // typing delay between keystrokes (super fast)
                                    cursor: '|'
                                }}
                            />
                        </Segment>
                    </div>
                </>
            )}


            {!cardData.ai_overview && (
                <>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '1.5em',
                        }}
                    >
                        <Header as="h2" style={{ margin: 0, display: 'flex', alignItems: 'center', paddingLeft: '0.1em' }}>
                            AI-Generated Summary
                            <Image
                                src={AIIcon}
                                alt="AI Icon"
                                style={{ marginLeft: '0.5em', width: '24px', height: '24px' }}
                            />
                        </Header>
                    </div>

                    <div
                        className={`overview-container ${showOverview ? 'show' : 'hide'}`}
                    >
                        <Segment className="gray-background">
                            {/* Blockquote disclaimer */}
                            <blockquote style={{ marginBottom: '0em'}}>
                                An AI-Generated Summary will be available once this professor has at least 20 approved reviews.
                            </blockquote>
                        </Segment>
                    </div>
                </>
            )}

            <SyllabusUploadModal
                professors={null}
                open={syllabusModalOpen}
                onClose={() => setSyllabusModalOpen(false)}
                courses={courses}
                onUploadComplete={() => setSyllabusModalOpen(false)}
            />
        </>
    );
};

export default ProfessorCard;
